import {
  Box,
  Grid,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReusableCard from "../../components/reusableComponents/ReusableCard";
import bgimage from "../../img/no_image.png";
import ReusableHeadingComponents from "../../components/reusableComponents/reusableHeadingComponents";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";
import ApiService from "../../_services/ApiService";
import { CardSkeletonCollab } from "../../components/reusableComponents/SkeletonCard";
import NoResults from "../../components/reusableComponents/NoResults";
import { formatEventDate } from "../../_helpers/reusablefunctions/dateSetter";
import { useHistory } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import Tablecomponent from "../../components/reusableComponents/Tablecomponent";

const EventDetails = () => {
  const history = useHistory();
  const [eventList, setEventList] = useState([]);
  const [loader, setLoader] = useState(true);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedTab, setSelectedTab] = useState(0);

  // 🔹 event types from API + selected category id
  const [eventTypes, setEventTypes] = useState([
    { id: null, CategoryName: "All" },
  ]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'table'
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  // 🔹 Fetch event types for tabs
  useEffect(() => {
    ApiService.get("eventsType")
      .then((res) => {
        const apiData = res?.data?.data || res?.data || [];
        const published = apiData.filter((t) => t.Published);
        // First tab is "All"
        setEventTypes([{ id: null, CategoryName: "All" }, ...published]);
      })
      .catch((err) => {
        console.error("Error fetching eventsType:", err);
      });
  }, []);

  // 🔹 Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(0); // reset to first page when search changes
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // 🔹 Fetch event list (with optional EventCategory filter & search)
  useEffect(() => {
    setLoader(true);

    const params = {
      offset: page,
      limit: rowsPerPage,
    };

    if (selectedCategoryId) {
      // backend expects EventCategory = id of eventsType
      params.EventCategory = selectedCategoryId;
    }

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    ApiService.get("eventList", params)
      .then((res) => {
        let response = res?.data?.data;
        if (res.status === 200 || res.status === 201) {
          setCount(res?.data?.count);
          setEventList(response || []);
        }
        setLoader(false);
      })
      .catch((error) => {
        setLoader(false);
        console.log(error);
      });
  }, [page, rowsPerPage, selectedCategoryId, debouncedSearch]);

  function openSubmission() {
    history.push("/event/submission");
  }

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) =>
    setRowsPerPage(parseInt(event.target.value, 10));

  // 🔹 Tab change
  const handleTabChange = (_e, val) => {
    setSelectedTab(val);
    const selected = eventTypes[val];
    setSelectedCategoryId(selected?.id || null); // null = All
    setPage(0);
  };

  // columns for Tablecomponent
  const columns = [
    {
      headerName: "Event Name",
      field: "EventName",
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={2}>
          <img
            src={row?.Featured_Banner || bgimage}
            alt="event"
            width={80}
            height={50}
            style={{ borderRadius: "8px", objectFit: "cover" }}
          />
          <strong>{row?.EventName || "--"}</strong>
        </Box>
      ),
    },
    {
      headerName: "Date",
      field: "date",
      renderCell: ({ row }) =>
        formatEventDate(row?.EventFromDate, row?.EventToDate),
    },
    {
      headerName: "Location",
      field: "location",
      renderCell: ({ row }) => row?.Location || "—",
    },
    {
      headerName: "Actions",
      field: "actions",
      renderCell: ({ row }) => (
        <Button
          variant="contained"
          size="small"
          sx={{
            textTransform: "none",
            backgroundColor: "#ED1C24",
            "&:hover": { backgroundColor: "#c3080d" },
            borderRadius: "6px",
            padding: "4px 12px",
            fontSize: "13px",
          }}
          onClick={() =>
            history.push("/event/view-event", { eventData: row })
          }
        >
          View
        </Button>
      ),
    },
  ];

  // 🔹 Search (front-end filter on top of API search, optional)
  const filteredEvents = eventList.filter((e) =>
    e.EventName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box p={4}>
      {/* Header Section */}
      <Box
        display="flex"
        alignItems={{ xs: "none", sm: "center" }}
        justifyContent="space-between"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          paddingBottom: { xs: "20px", sm: 0 },
        }}
      >
        <ReusableHeadingComponents
          heading="Events Sponsorship"
          subHeading="Connect with sponsors and unlock opportunities to fuel your next big event."
        />
        <ReusableButton
          children="My Submission"
          variant="contained"
          classNames="gn-actionbtn"
          onClick={openSubmission}
        />
      </Box>

      {/* Search + Toggle */}
      <Box
        mt={1}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          placeholder="Search Event"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: "500px" },
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#7A7A7A" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" alignItems="center">
          <Tooltip title="Grid View">
            <IconButton
              onClick={() => setViewMode("grid")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
                width: 40,
                height: 40,
                backgroundColor: viewMode === "grid" ? "#7A7A7A" : "transparent",
                color: viewMode === "grid" ? "#FFFFFF" : "#7A7A7A",
                "&:hover": {
                  backgroundColor: viewMode === "grid" ? "#6A6A6A" : "#f0f0f0",
                },
              }}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Table View">
            <IconButton
              onClick={() => setViewMode("table")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRadius: "0 8px 8px 0",
                width: 40,
                height: 40,
                backgroundColor: viewMode === "table" ? "#7A7A7A" : "transparent",
                color: viewMode === "table" ? "#FFFFFF" : "#7A7A7A",
                "&:hover": {
                  backgroundColor: viewMode === "table" ? "#6A6A6A" : "#f0f0f0",
                },
              }}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs (from eventsType) */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          mb: 3,
          backgroundColor: "#FFFFFF",
          pl: "16px",
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant={isMobileOrTablet ? "scrollable" : "standard"}
          scrollButtons={isMobileOrTablet ? "auto" : false}
          allowScrollButtonsMobile={isMobileOrTablet}
          sx={{
            minHeight: "45px",
            "& .MuiTabs-flexContainer": {
              gap: 1,
            },
            "& .MuiTabs-scrollContainer": {
              marginLeft: "0 !important",
              paddingLeft: "0 !important",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: "32px",
              padding: "8px 16px",
              borderRadius: "8px",
              color: "#7A7A7A",
              margin: "6px 0",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .Mui-selected": {
              backgroundColor: "#F14950 !important",
              color: "#FFFFFF !important",
              minHeight: "32px",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {eventTypes.map((tab) => (
            <Tab
              key={tab.id ?? "all"}
              label={tab.CategoryName || tab.TypeName}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Conditional View */}
      {viewMode === "grid" ? (
        <Box mb={4}>
          <Grid container spacing={2}>
            {loader ? (
              Array.from({ length: 8 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  <CardSkeletonCollab />
                </Grid>
              ))
            ) : filteredEvents.length ? (
              filteredEvents.map((data, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <ReusableCard
                    image={data?.Featured_Banner || bgimage}
                    title={data?.EventName || ""}
                    date={formatEventDate(
                      data?.EventFromDate,
                      data?.EventToDate
                    )}
                    location={data?.Location || ""}
                    secondaryAction={{
                      label: "View",
                      onClick: () =>
                        history.push("/event/view-event", {
                          eventData: data,
                        }),
                    }}
                  />
                </Grid>
              ))
            ) : (
              <NoResults eventNoData="No Event Found" />
            )}
          </Grid>
        </Box>
      ) : (
        <Box>
          <Tablecomponent
            loader={loader}
            columns={columns}
            data={filteredEvents}
            rowsPerPage={rowsPerPage}
            page={page}
            count={count}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[5, 10, 20]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}
    </Box>
  );
};

export default EventDetails;
